import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Wifi, WifiOff } from 'lucide-react';
import { vendorPortalApi } from '../../api/endpoints/vendor-portal';
import { Card, StatCard } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Table, Thead, Th, Td, Tr } from '../../components/ui/Table';
import { Pagination } from '../../components/ui/Pagination';
import { MetricBar } from '../../components/ui/MetricBar';
import { PageSpinner } from '../../components/ui/Spinner';
import { formatTimeAgo, formatDuration, formatUptime } from '../../utils/formatters';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

export function VendorDeviceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const deviceId = id;
  const [timelineHours, setTimelineHours] = useState(24);
  const [chartHours, setChartHours] = useState(24);
  const [eventsPage, setEventsPage] = useState(1);
  const eventsPerPage = 10;

  const { data: deviceData, isLoading } = useQuery({
    queryKey: ['vendor-device', deviceId],
    queryFn: () => vendorPortalApi.getDevice(deviceId!),
    enabled: !!deviceId,
    refetchInterval: 30000,
  });

  const { data: metricsData } = useQuery({
    queryKey: ['vendor-device-metrics', deviceId, chartHours],
    queryFn: () => vendorPortalApi.getDeviceMetrics(deviceId!, chartHours),
    enabled: !!deviceId,
  });

  const { data: timelineData } = useQuery({
    queryKey: ['vendor-device-timeline', deviceId, timelineHours],
    queryFn: () => vendorPortalApi.getDeviceTimeline(deviceId!, timelineHours),
    enabled: !!deviceId,
  });

  if (isLoading) return <PageSpinner />;

  const device = deviceData?.device;
  if (!device) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 dark:text-gray-400">Device not found</p>
        <button onClick={() => navigate('/vendor/dashboard')} className="text-teal-600 text-sm mt-2">Back to Dashboard</button>
      </div>
    );
  }

  const metricsHistory = metricsData?.metrics || [];
  const analytics = timelineData?.analytics;
  const offlineEvents = analytics?.offline_events?.slice().reverse() || [];
  const totalEventPages = Math.ceil(offlineEvents.length / eventsPerPage);
  const pagedEvents = offlineEvents.slice((eventsPage - 1) * eventsPerPage, eventsPage * eventsPerPage);

  const statusVariant = device.status === 'connected' ? 'green' : device.status === 'disconnected' ? 'red' : 'yellow';
  const statusLabel = device.status === 'connected' ? 'Online' : device.status === 'disconnected' ? 'Offline' : device.status;

  const m = device.metrics;

  return (
    <div>
      {/* Header */}
      <div className="mb-4">
        <button
          onClick={() => navigate('/vendor/dashboard')}
          className="inline-flex items-center text-teal-600 dark:text-teal-400 hover:text-teal-800 dark:hover:text-teal-300 text-xs font-medium mb-3"
        >
          <ArrowLeft size={16} className="mr-1" />
          Back to Dashboard
        </button>
        <div className="flex items-center gap-3">
          <div className={`w-3.5 h-3.5 rounded-full ${
            device.status === 'connected' ? 'bg-green-500 animate-pulse' : device.status === 'disconnected' ? 'bg-red-500' : 'bg-yellow-500'
          }`} />
          <h1 className="text-lg font-semibold text-gray-800 dark:text-white">{device.name}</h1>
          <Badge variant={statusVariant} dot>{statusLabel}</Badge>
        </div>
      </div>

      {/* Uptime Overview */}
      {analytics && (
        <Card
          title="Uptime Overview"
          className="mb-4"
          action={
            <select
              value={timelineHours}
              onChange={e => setTimelineHours(Number(e.target.value))}
              className="text-xs border border-gray-300 dark:border-gray-600 rounded px-2 py-1 dark:bg-gray-700 dark:text-white focus:ring-1 focus:ring-teal-500"
            >
              <option value={6}>6h</option>
              <option value={24}>24h</option>
              <option value={48}>48h</option>
              <option value={168}>7d</option>
              <option value={720}>30d</option>
            </select>
          }
        >
          <div className="p-4">
            <div className="grid grid-cols-5 gap-4 mb-4">
              <StatCard
                label="Uptime"
                value={`${analytics.uptime_percent.toFixed(1)}%`}
                color={analytics.uptime_percent >= 99 ? 'green' : analytics.uptime_percent >= 95 ? 'yellow' : 'red'}
              />
              <StatCard
                label="Downtime"
                value={formatDuration(analytics.total_downtime_ms)}
                color={analytics.total_downtime_ms > 3600000 ? 'red' : analytics.total_downtime_ms > 0 ? 'yellow' : 'green'}
              />
              <StatCard
                label="Disconnections"
                value={analytics.disconnect_count}
                color={analytics.disconnect_count > 10 ? 'red' : analytics.disconnect_count > 0 ? 'yellow' : 'green'}
              />
              <StatCard label="Avg Down" value={analytics.disconnect_count > 0 ? formatDuration(analytics.avg_downtime_ms) : '--'} color="gray" />
              <StatCard label="Longest" value={analytics.longest_downtime_ms > 0 ? formatDuration(analytics.longest_downtime_ms) : '--'} color="gray" />
            </div>

            {/* Timeline bar */}
            <TimelineBar
              changes={timelineData?.changes || []}
              period={timelineData?.period}
              deviceStatus={device.status}
            />
          </div>
        </Card>
      )}

      {/* Resource Chart + Health */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 mb-4">
        <Card
          title="Resource Usage"
          className="lg:col-span-3"
          action={
            <select
              value={chartHours}
              onChange={e => setChartHours(Number(e.target.value))}
              className="text-xs border border-gray-300 dark:border-gray-600 rounded px-2 py-1 dark:bg-gray-700 dark:text-white focus:ring-1 focus:ring-teal-500"
            >
              <option value={6}>6 Hours</option>
              <option value={24}>24 Hours</option>
              <option value={168}>7 Days</option>
              <option value={720}>30 Days</option>
            </select>
          }
        >
          <div className="p-4 h-64">
            {metricsHistory.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={metricsHistory}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis
                    dataKey="timestamp"
                    tickFormatter={t => new Date(t).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    tick={{ fontSize: 10 }}
                  />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} />
                  <Tooltip
                    contentStyle={{ fontSize: 11 }}
                    labelFormatter={t => new Date(t).toLocaleString()}
                  />
                  <Line type="monotone" dataKey="cpu_percent" stroke="#3b82f6" name="CPU" dot={false} strokeWidth={1.5} />
                  <Line type="monotone" dataKey="memory_percent" stroke="#8b5cf6" name="Memory" dot={false} strokeWidth={1.5} />
                  <Line type="monotone" dataKey="disk_percent" stroke="#f59e0b" name="Disk" dot={false} strokeWidth={1.5} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full text-sm text-gray-400 dark:text-gray-500">No metrics data</div>
            )}
          </div>
        </Card>

        <Card title="Health" className="lg:col-span-2">
          <div className="p-4 space-y-3">
            {m && m.collected_at ? (
              <>
                {/* Tunnel + Latency + Uptime + Reconnects */}
                <div className="grid grid-cols-2 gap-2">
                  <div className={`rounded-lg p-2 text-center ${
                    m.tunnel_status === 'healthy' ? 'bg-green-50 dark:bg-green-900/20' :
                    m.tunnel_status === 'degraded' ? 'bg-yellow-50 dark:bg-yellow-900/20' :
                    'bg-red-50 dark:bg-red-900/20'
                  }`}>
                    <p className="text-[10px] text-gray-500 dark:text-gray-400">Tunnel</p>
                    <p className={`text-sm font-semibold ${
                      m.tunnel_status === 'healthy' ? 'text-green-700 dark:text-green-400' :
                      m.tunnel_status === 'degraded' ? 'text-yellow-700 dark:text-yellow-400' :
                      'text-red-700 dark:text-red-400'
                    }`}>{m.tunnel_status || 'Unknown'}</p>
                  </div>
                  <div className="rounded-lg bg-blue-50 dark:bg-blue-900/20 p-2 text-center">
                    <p className="text-[10px] text-gray-500 dark:text-gray-400">Latency</p>
                    <p className={`text-sm font-semibold ${
                      (m.latency_ms || 0) > 200 ? 'text-red-600 dark:text-red-400' :
                      (m.latency_ms || 0) > 100 ? 'text-yellow-600 dark:text-yellow-400' :
                      'text-green-600 dark:text-green-400'
                    }`}>{m.latency_ms != null ? `${m.latency_ms}ms` : 'N/A'}</p>
                  </div>
                  <div className="rounded-lg bg-teal-50 dark:bg-teal-900/20 p-2 text-center">
                    <p className="text-[10px] text-gray-500 dark:text-gray-400">Uptime</p>
                    <p className="text-sm font-semibold text-teal-700 dark:text-teal-400">{m.uptime_seconds ? formatUptime(m.uptime_seconds) : 'N/A'}</p>
                  </div>
                  <div className="rounded-lg bg-gray-50 dark:bg-gray-900/50 p-2 text-center">
                    <p className="text-[10px] text-gray-500 dark:text-gray-400">Reconnects</p>
                    <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">{m.reconnect_count || 0}</p>
                  </div>
                  <div className={`rounded-lg p-2 text-center ${m.mongodb_status === 'up' ? 'bg-green-50 dark:bg-green-900/20' : m.mongodb_status === 'down' ? 'bg-red-50 dark:bg-red-900/20' : 'bg-gray-50 dark:bg-gray-900/50'}`}>
                    <p className="text-[10px] text-gray-500 dark:text-gray-400">MongoDB</p>
                    <p className={`text-sm font-semibold ${m.mongodb_status === 'up' ? 'text-green-700 dark:text-green-400' : m.mongodb_status === 'down' ? 'text-red-700 dark:text-red-400' : 'text-gray-500'}`}>{m.mongodb_status || '-'}</p>
                  </div>
                  <div className={`rounded-lg p-2 text-center ${m.pm2_status?.includes('online') ? 'bg-green-50 dark:bg-green-900/20' : m.pm2_status?.includes('errored') ? 'bg-red-50 dark:bg-red-900/20' : 'bg-gray-50 dark:bg-gray-900/50'}`}>
                    <p className="text-[10px] text-gray-500 dark:text-gray-400">PM2</p>
                    <p className={`text-xs font-semibold ${m.pm2_status?.includes('errored') ? 'text-red-700 dark:text-red-400' : m.pm2_status?.includes('online') ? 'text-green-700 dark:text-green-400' : 'text-gray-500'}`}>{m.pm2_status || '-'}</p>
                  </div>
                </div>

                {/* Metric bars */}
                <MetricBar value={m.cpu_percent} label="CPU" size="md" color="blue" />
                <MetricBar value={m.memory_percent} label="MEM" size="md" color="indigo" />
                <MetricBar value={m.disk_percent} label="DISK" size="md" color="purple" />

                {/* Last error */}
                {m.last_error && m.last_error !== 'None' && m.last_error !== '' && (
                  <div className="mt-2 p-2 bg-red-50 dark:bg-red-900/20 rounded text-xs text-red-600 dark:text-red-400 font-mono break-words">
                    {m.last_error}
                  </div>
                )}

                <p className="text-[10px] text-gray-400 dark:text-gray-500 text-right">
                  Last collected: {new Date(m.collected_at).toLocaleTimeString()}
                </p>
              </>
            ) : (
              <div className="text-center py-4">
                <p className="text-xs text-gray-400 dark:text-gray-500">No monitoring data yet</p>
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Offline Events */}
      {offlineEvents.length > 0 && (
        <Card title="Offline Events" className="mb-4" action={
          <span className="text-xs text-gray-400 dark:text-gray-500">{offlineEvents.length} event{offlineEvents.length > 1 ? 's' : ''}</span>
        }>
          <Table>
            <Thead>
              <tr>
                <Th>Went Offline</Th>
                <Th>Came Online</Th>
                <Th>Duration</Th>
              </tr>
            </Thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
              {pagedEvents.map((evt, i) => {
                const durColor = evt.duration_ms > 1800000 ? 'text-red-600 dark:text-red-400 font-semibold' :
                  evt.duration_ms > 300000 ? 'text-yellow-600 dark:text-yellow-400 font-semibold' : 'text-gray-700 dark:text-gray-300';
                return (
                  <Tr key={i}>
                    <Td>
                      <span className="flex items-center gap-1 text-red-600 dark:text-red-400">
                        <WifiOff size={12} />
                        {new Date(evt.start).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                      </span>
                    </Td>
                    <Td>
                      {evt.end ? (
                        <span className="flex items-center gap-1 text-green-600 dark:text-green-400">
                          <Wifi size={12} />
                          {new Date(evt.end).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                        </span>
                      ) : (
                        <span className="text-red-500 text-xs font-medium">Still offline</span>
                      )}
                    </Td>
                    <Td><span className={durColor}>{formatDuration(evt.duration_ms)}</span></Td>
                  </Tr>
                );
              })}
            </tbody>
          </Table>
          {totalEventPages > 1 && (
            <div className="px-4 pb-3">
              <Pagination page={eventsPage} totalPages={totalEventPages} onPageChange={setEventsPage} color="teal" />
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

// --- Timeline Bar ---

function TimelineBar({ changes, period, deviceStatus }: {
  changes: Array<{ status: string; timestamp: string }>;
  period?: { since: string; until: string };
  deviceStatus: string;
}) {
  if (!period) return null;
  const start = new Date(period.since).getTime();
  const end = new Date(period.until).getTime();
  const total = end - start;
  if (total <= 0) return null;

  if (changes.length === 0) {
    const bgColor = deviceStatus === 'connected' ? 'bg-emerald-500' : deviceStatus === 'disconnected' ? 'bg-rose-600' : 'bg-gray-300 dark:bg-gray-600';
    return (
      <div>
        <div className="relative h-6 rounded-md overflow-hidden bg-gray-200 dark:bg-gray-700">
          <div className={`h-full w-full ${bgColor}`} />
        </div>
        <div className="flex justify-between mt-1">
          <span className="text-[10px] text-gray-400">{new Date(period.since).toLocaleString()}</span>
          <span className="text-[10px] text-gray-400">{new Date(period.until).toLocaleString()}</span>
        </div>
      </div>
    );
  }

  const segments: Array<{ left: number; width: number; online: boolean }> = [];
  let lastTime = start;
  let lastOnline = changes[0].status !== 'connected'; // infer: first change goes TO connected -> was disconnected before

  for (const change of changes) {
    const t = new Date(change.timestamp).getTime();
    if (t > lastTime && t <= end) {
      segments.push({
        left: ((lastTime - start) / total) * 100,
        width: ((t - lastTime) / total) * 100,
        online: lastOnline,
      });
      lastTime = t;
    }
    lastOnline = change.status === 'connected';
  }
  segments.push({
    left: ((lastTime - start) / total) * 100,
    width: ((end - lastTime) / total) * 100,
    online: lastOnline,
  });

  return (
    <div>
      <div className="relative h-6 rounded-md overflow-hidden bg-gray-200 dark:bg-gray-700">
        {segments.map((seg, i) => (
          <div
            key={i}
            className={seg.online ? 'bg-emerald-500' : 'bg-rose-600'}
            style={{ position: 'absolute', left: `${seg.left}%`, width: `${seg.width}%`, height: '100%' }}
          />
        ))}
      </div>
      <div className="flex justify-between mt-1">
        <span className="text-[10px] text-gray-400">{new Date(period.since).toLocaleString()}</span>
        <span className="text-[10px] text-gray-400">{new Date(period.until).toLocaleString()}</span>
      </div>
    </div>
  );
}
